import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesAPI, usersAPI } from '../services/api';
import { fmt, STATUS_LABELS, TIER_LABELS, FAMILY_LABELS, NO_FAMILY } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';

export default function QuotesPage() {
  const [quotes, setQuotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [seller, setSeller]   = useState('');
  const [sellers, setSellers] = useState([]);
  const navigate = useNavigate();
  const [showFamily, setShowFamily] = useState(false);
  const [creating, setCreating]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await quotesAPI.list({ search: search || undefined, status: status || undefined, salesperson_id: seller || undefined });
      setQuotes(data);
    } catch { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, status, seller]); // eslint-disable-line

  // Lista de vendedores para el filtro. El endpoint es manager-only, asi que a
  // un vendedor le regresa 403 y el selector simplemente no aparece.
  useEffect(() => {
    usersAPI.salespeople().then(r => setSellers(r.data)).catch(() => setSellers([]));
  }, []);

  // Paso 3 familia (21-oct-2026): la familia se escoge ANTES de crear. NO se manda customer_id aqui;
  // el cliente se pone despues en el detalle (PUT), que no toca Odoo.
  const handleNew = async (job_family) => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await quotesAPI.create({ notes: '', job_family });
      navigate(`/quotes/${data.id}`);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'Failed to create quote');
    } finally { setCreating(false); }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <h2>Quotes</h2>
            <div className="page-subtitle">{quotes.length} quote{quotes.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowFamily(true)}>+ New Quote</button>
        </div>

        <div className="page-body">
          {/* Filters */}
          <div className="flex gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
            <div className="search-bar" style={{ flex: 1, minWidth: 220 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                className="search-input"
                placeholder="Search by quote #, Odoo SO, customer..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="form-select" style={{ width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="closed_won">Won</option>
              <option value="closed_lost">Lost</option>
            </select>
            {sellers.length > 0 && (
              <select className="form-select" style={{ width: 180 }}
                value={seller} onChange={e => setSeller(e.target.value)}>
                <option value="">All salespeople</option>
                {sellers.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
          </div>

          {/* Table */}
          <div className="card" style={{ padding: 0 }}>
            {loading ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : quotes.length === 0 ? (
              <div className="empty-state">
                <h3>No quotes found</h3>
                <p>Create your first quote to get started.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Quote #</th>
                      <th>Job type</th>
                      <th>Customer</th>
                      <th>Tier</th>
                      <th>Salesperson</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map(q => {
                      const st = STATUS_LABELS[q.status] || STATUS_LABELS.draft;
                      const tr = TIER_LABELS[q.tier] || TIER_LABELS.T1;
                      return (
                        <tr key={q.id} className="clickable" onClick={() => navigate(`/quotes/${q.id}`)}>
                          <td>
                            <span className="text-mono" style={{ color: 'var(--text)', fontWeight: 600 }}>
                              {q.quote_number}
                            </span>
                            {q.version > 1 && <span className="badge badge-gray" style={{ marginLeft: 6 }}>v{q.version}</span>}
                          </td>
                          <td>{(() => { const f = FAMILY_LABELS[q.job_family] || NO_FAMILY;
                            return <span className={`badge ${f.cls}`}>{f.label}</span>; })()}</td>
                          <td style={{ color: 'var(--text)' }}>{q.customer_name || <span className="text-muted">—</span>}</td>
                          <td><span className={`badge ${tr.cls}`}>{q.tier || 'T1'}</span></td>
                          <td>{q.salesperson_name}</td>
                          <td className="text-muted text-sm">{fmt.date(q.created_at)}</td>
                          <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                          <td className="text-mono" style={{ color: 'var(--text)', fontWeight: 600 }}>
                            {fmt.currency(q.grand_total)}
                          </td>
                          <td>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                 stroke="var(--text-3)" strokeWidth="2">
                              <path d="M9 18l6-6-6-6"/>
                            </svg>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {showFamily && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">New quote: what kind of job is this?</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowFamily(false)}>X</button>
            </div>
            <div className="modal-body">
              <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                Everything in this quote (pieces, labor, services, freight) is commissioned
                as the job type you pick here. You can still add a piece of the other type.
              </p>
              <div className="flex gap-3">
                <button className="btn btn-primary" style={{ flex: 1, padding: '16px 8px' }}
                  disabled={creating} onClick={() => handleNew('iron')}>Iron Doors</button>
                <button className="btn btn-primary" style={{ flex: 1, padding: '16px 8px' }}
                  disabled={creating} onClick={() => handleNew('alu')}>Aluminum Windows &amp; Doors</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
